import { TooltipHost } from '@fluentui/react';
import React, { forwardRef } from 'react';
import { IStatusAction } from '../../helpers/userHooks/userTypes';
import { useWindowWidth } from 'shared-state/display-options/hooks';

export interface ICircularActionButtonProps {
    action: IStatusAction;
    onCustomClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Circular colored action button with hover effects.
 * Active state: colored ring border + grey fill + black icon
 * Inactive state: solid status color + white icon
 */
const CircularActionButton = forwardRef<HTMLDivElement, ICircularActionButtonProps>(({ action, onCustomClick }, ref) => {
    const { smallDevice } = useWindowWidth();
    const hasRing = !!action.borderColor;

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (onCustomClick) {
            onCustomClick(e);
        } else {
            action.action();
        }
    };

    return (
        <TooltipHost content={smallDevice ? '' : action.title}>
            <div
                ref={ref}
                className="pressable"
                onClick={handleClick}
                style={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: '50%',
                    backgroundColor: action.color,
                    border: hasRing ? `3px solid ${action.borderColor}` : 'none',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    boxShadow: hasRing ? `0 0 6px ${action.borderColor}80` : '0 2px 4px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = hasRing ? `0 0 10px ${action.borderColor}` : '0 4px 8px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = hasRing ? `0 0 6px ${action.borderColor}80` : '0 2px 4px rgba(0,0,0,0.15)';
                }}
            >
                {action.onRenderIcon && action.onRenderIcon()}
            </div>
        </TooltipHost>
    );
});

CircularActionButton.displayName = 'CircularActionButton';

export default CircularActionButton;
